import React from 'react';
import * as Sentry from '@sentry/react';

type SentryConfig = {
  dsn: string;
  environment: string;
  release: string | null;
  sampleRate?: number;
};

const parseSampleRate = (): number => {
  const raw = window.__SENTRY__?.sampleRate;
  if (typeof raw !== 'number' || Number.isNaN(raw)) {
    return 1;
  }

  return Math.max(0, Math.min(1, raw));
};

declare global {
  interface Window {
    __SENTRY__?: SentryConfig;
    __TPCT_SENTRY_INITIALIZED__?: boolean;
  }
}

export const initSentry = (): void => {
  const config = window.__SENTRY__;
  if (!config?.dsn || window.__TPCT_SENTRY_INITIALIZED__) {
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release ?? undefined,
    tracesSampleRate: 0,
    sampleRate: parseSampleRate(),
    debug: false,
    // Avoid Sentry monkey-patching browser globals across independently loaded admin apps.
    defaultIntegrations: false,
    integrations: [],
    // Strip sensitive fields before sending any event.
    beforeSend(event) {
      if (event.request?.headers) {
        const blocked = ['Authorization', 'Cookie', 'X-WP-Nonce'];
        blocked.forEach((h) => {
          if (event.request?.headers?.[h]) {
            event.request.headers[h] = '[Filtered]';
          }
        });
      }

      const message =
        (typeof event.message === 'string' ? event.message : '') ||
        event.exception?.values?.[0]?.value ||
        '';
      const noisePatterns = [
        /^Script error\.?$/i,
        /ResizeObserver loop limit exceeded/i,
        /Non-Error promise rejection captured/i,
        /Loading chunk [\d]+ failed/i,
      ];
      if (noisePatterns.some((pattern) => pattern.test(message))) {
        return null;
      }

      const requestUrl = event.request?.url ?? '';
      if (/\/(wp-login\.php|xmlrpc\.php)(\?|$)/i.test(requestUrl)) {
        return null;
      }

      return event;
    },
  });

  window.__TPCT_SENTRY_INITIALIZED__ = true;
};

// Cast avoids TS class-component refs mismatch between @sentry/react and @types/react v18/v19
export const SentryErrorBoundary = Sentry.ErrorBoundary as unknown as React.ComponentType<{
  fallback: React.ReactNode;
  children: React.ReactNode;
}>;

export { Sentry };
